import styled from 'styled-components';
import IntervalDatePicker from 'components/IntervalDatePicker';
import SingleValuePicker from 'components/SingleValuePicker';
import ChipMultiSelect from 'components/ChipMultiSelect';
import DatePicker from 'components/DatePicker';
import { FilterProps } from 'types';

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end; /* Align all filters to bottom baseline */
  padding: 20px 32px;
  border-bottom: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? '1px solid rgba(148, 163, 184, 0.12)'
      : '1px solid rgba(226, 232, 240, 0.6)'};
  background: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.2) 0%, transparent 100%)'
      : 'linear-gradient(180deg, rgba(248, 250, 252, 0.4) 0%, transparent 100%)'};
  flex-wrap: wrap;
  min-height: 80px; /* Consistent height */

  /* Make form controls consistent and aligned */
  .MuiFormControl-root {
    min-width: 200px;
    max-width: 280px;
    margin: 0; /* Remove any default margins */
  }

  /* Consistent input styling with borders */
  .MuiInputBase-root {
    font-size: 0.875rem;
    background: ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.8)'};
    border: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? '1px solid rgba(148, 163, 184, 0.2)'
        : '1px solid rgba(203, 213, 225, 0.6)'};
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: ${(props) =>
        props.theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 1)'};
      border-color: ${(props) =>
        props.theme.palette.mode === 'dark'
          ? 'rgba(148, 163, 184, 0.4)'
          : 'rgba(100, 116, 139, 0.8)'};
    }

    &.Mui-focused {
      border-color: ${(props) =>
        props.theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.8)' : 'rgba(37, 99, 235, 0.8)'};
    }

    /* Remove MUI's default fieldset border to prevent double borders */
    fieldset {
      border: none;
    }
  }

  /* Consistent label styling */
  .MuiInputLabel-root {
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 4px;
  }

  /* Ensure all filter containers align properly */
  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterSectionsHash = {
  /** Generic */
  singleValuePicker: {
    Element: SingleValuePicker,
  },
  datePicker: {
    Element: DatePicker,
  },
  chipMultiSelect: {
    Element: ChipMultiSelect,
  },
  /** Specific */
  intervalDatePicker: {
    Element: IntervalDatePicker,
  },
};

const Filters = (props: FilterProps | any) => {
  const sections = props.filterSections || [];
  const filtersProps = props.filtersProps || {};
  const actions = filtersProps.actions;

  return (
    <FiltersContainer>
      {sections.map((section: string) => {
        const Filter = FilterSectionsHash[section as keyof typeof FilterSectionsHash].Element;
        const filterSpecificProps = filtersProps[section];
        return <Filter {...filterSpecificProps} key={section} />;
      })}
      {actions ? (
        <div
          className="filters-actions"
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 8,
          }}
        >
          {actions}
        </div>
      ) : null}
    </FiltersContainer>
  );
};

export default Filters;
